import React from "react";
import {
  Chip,
  Tooltip,
  IconButton,
  Typography,
  Stack,
} from "@mui/material";
import { Close, Visibility, OpenInNew, Search } from "@mui/icons-material";
import { DocumentChunk } from "../../types/interfaces/IConversationSettings";

export interface RAGDocumentChipProps {
  chunk: DocumentChunk;
  onRemove: (id: string) => void;
  onPreview: (chunk: DocumentChunk) => void;
}

export const RAGDocumentChip: React.FC<RAGDocumentChipProps> = ({
  chunk,
  onRemove,
  onPreview,
}) => {
  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRemove(chunk.id);
  };

  const handlePreview = (event: React.MouseEvent) => {
    event.stopPropagation();
    onPreview(chunk);
  };

  const handleOpenUrl = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (chunk.url) {
      window.open(chunk.url, '_blank');
    }
  };

  const getChipLabel = () => {
    const title = chunk.document_title && chunk.document_title !== 'Unknown Document' 
      ? chunk.document_title 
      : chunk.document_source || 'Document';
    
    const maxLength = window.innerWidth < 768 ? 15 : 25;
    
    if (title.length > maxLength) {
      return title.substring(0, maxLength) + "...";
    }
    return title;
  };

  const getTooltipContent = () => {
    const lines = [];
    lines.push(`Source: ${chunk.document_source}`);
    if (chunk.score !== undefined) {
      lines.push(`Relevance: ${(chunk.score * 100).toFixed(1)}%`);
    }
    if (chunk.content_length) {
      lines.push(`Length: ${chunk.content_length} chars`);
    }
    if (chunk.metadata?.author) {
      lines.push(`Author: ${chunk.metadata.author}`);
    }
    if (chunk.metadata?.content_stats?.word_count) {
      lines.push(`Words: ${chunk.metadata.content_stats.word_count}`);
    }
    return lines.join('\n');
  };

  return (
    <Tooltip title={getTooltipContent()}>
      <Chip
        variant="outlined"  
        onDelete={handleRemove}
        icon={<Search />}
        label={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" noWrap>
              {getChipLabel()}
            </Typography>
            <Stack direction="row" spacing={0.25}>
              <IconButton
                size="small"
                onClick={handlePreview}
                sx={{ padding: "2px" }}
              >
                <Visibility fontSize="small" />
              </IconButton>
              {chunk.url && (
                <IconButton
                  size="small"
                  onClick={handleOpenUrl}
                  sx={{ padding: "2px" }}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Stack>
        }
        sx={{
          "& .MuiChip-label": {
            padding: "0 8px",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiIconButton-root": {
            margin: 0,
            padding: "2px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          },
        }}
      />
    </Tooltip>
  );
}; 